import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { NotifierService } from "angular-notifier";
import { Subscription } from "rxjs";
import { FormField } from "src/app/interfaces/form-field.model";
import { VehicleStockAvailabilityService } from "src/app/services/vehicle/vehicle-stock-availability.service";

interface MonthlyStock {
  Id: number;
  MonthName: string;
  ItemList: {
    ExtColourCode: string;
    IntColourCode: string;
    Quantity: number;
  }[];
}

interface StockRow {
  ExtColourCode: string;
  ExtColourDescription: string;
  IntColourCode: string;
  IntColourDescription: string;
  Type: string;
  InStock: number;
}

@Component({
  selector: "app-vehicle-stock-availability",
  templateUrl: "./vehicle-stock-availability.component.html",
  styleUrls: ["./vehicle-stock-availability.component.css"],
})
export class VehicleStockAvailabilityComponent
  implements OnInit, OnDestroy
{
  upperFormFields: FormField[] = [
    {
      type: "select",
      id: "model",
      label: "Select Model",
      options: [],
      value: "",
    },
  ];
  models: { value: string; display: string }[] = [];
  selectedModelCode = "";
  tableVisible = false;
  tableHeaders: any[] = [];
  tableEntries: any[] = [];
  loading = false;
  statusMessage = "";
  private stockRequest?: Subscription;
  private modelsRequest?: Subscription;

  constructor(
    private vehicleStockAvailabilityService: VehicleStockAvailabilityService,
    private router: Router,
    private notifier: NotifierService,
  ) {}

  ngOnInit(): void {
    this.modelsRequest = this.vehicleStockAvailabilityService
      .getModelActiveInSalesDrpList()
      .subscribe({
        next: (response) => {
          if (
            response.statusCode !== 200 ||
            !Array.isArray(response.body)
          ) {
            this.showError(
              "Unable to load models. Please reload the page to try again.",
            );
            return;
          }
          this.models = response.body.map(
            (model: { id: string; name: string }) => ({
              value: model.id,
              display: model.name,
            }),
          );
          this.upperFormFields = [
            { ...this.upperFormFields[0], options: this.models },
          ];
        },
        error: () =>
          this.showError(
            "Unable to load models. Please reload the page to try again.",
          ),
      });
  }

  handleUpdate(event: { fields: FormField[] }): void {
    this.stockRequest?.unsubscribe();
    this.tableVisible = false;
    this.tableEntries = [];
    this.tableHeaders = [];
    this.statusMessage = "";
    this.loading = false;
    const value = event.fields.find(
      (field) => field.id === "model",
    )?.value;
    const model = this.models.find(
      (option) => option.value === value,
    );
    this.selectedModelCode = model?.value ?? "";
    if (!model) return;

    this.loading = true;
    this.stockRequest = this.vehicleStockAvailabilityService
      .getVehicleStockAvailability(model.display)
      .subscribe({
        next: (response) => {
          this.loading = false;
          if (
            response.statusCode !== 200 ||
            !Array.isArray(response.body?.GridData) ||
            !Array.isArray(response.body?.ExtraData)
          ) {
            this.showError(
              "Unable to load vehicle stock availability. Please select the model again to retry.",
            );
            return;
          }
          const months: MonthlyStock[] = response.body.ExtraData;
          this.tableHeaders = [
            {
              title: "EXTERIOR COLOUR",
              type: "link",
              id: "extColour",
            },
            { title: "TYPE", type: "data", id: "type" },
            { title: "INT COLOUR", type: "data", id: "intColour" },
            {
              title: "IN STOCK",
              type: "data",
              id: "inStock",
              classes: "text-red-600 text-center",
              Hclasses: "text-red-600",
            },
            ...months.map((month) => ({
              title: `ETA (MID ${this.stockMonthInEnglish(month)})`,
              type: "data",
              id: `eta_${month.Id}`,
              classes: "text-center",
            })),
          ];
          this.tableEntries = response.body.GridData.map(
            (item: StockRow) => {
              const extColour = `${item.ExtColourCode} - ${item.ExtColourDescription}`;
              const intColour = `${item.IntColourCode} - ${item.IntColourDescription}`;
              const row: Record<string, string | number> = {
                colourCode: item.ExtColourCode,
                extColour,
                intColour,
                type: item.Type,
                inStock: item.InStock,
              };
              for (const month of months) {
                row[`eta_${month.Id}`] = month.ItemList.filter(
                  (stock) =>
                    stock.ExtColourCode === extColour &&
                    stock.IntColourCode === intColour,
                ).reduce((total, stock) => total + stock.Quantity, 0);
              }
              return row;
            },
          );
          this.tableVisible = this.tableEntries.length > 0;
          this.statusMessage = this.tableVisible
            ? ""
            : "No stock availability found for this model.";
        },
        error: () => {
          this.loading = false;
          this.showError(
            "Unable to load vehicle stock availability. Please select the model again to retry.",
          );
        },
      });
  }

  openEntry(entry: { colourCode: string }): void {
    this.router.navigate(["/vehicle/vehicle-selection"], {
      state: {
        modelCode: this.selectedModelCode,
        colourCode: entry.colourCode,
      },
    });
  }

  private showError(message: string): void {
    this.statusMessage = message;
    this.notifier.notify("error", message);
  }

  private stockMonthInEnglish(month: { MonthName: string }): string {
    const prefix = String(month.MonthName ?? "").trim().toUpperCase().slice(0, 3);
    const spanishMonths = [
      "ENE", "FEB", "MAR", "ABR", "MAY", "JUN",
      "JUL", "AGO", "SEP", "OCT", "NOV", "DIC",
    ];
    const englishMonths = [
      "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
      "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
    ];
    const index = spanishMonths.indexOf(prefix === "SET" ? "SEP" : prefix);
    return index >= 0 ? englishMonths[index] : prefix;
  }

  ngOnDestroy(): void {
    this.stockRequest?.unsubscribe();
    this.modelsRequest?.unsubscribe();
  }
}
